import { useState, useEffect, useRef } from 'react';
import { Box, Typography, IconButton, Skeleton, Tooltip, Select, MenuItem, FormControl } from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import SyncIcon from '@mui/icons-material/Sync';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import MoodIcon from '@mui/icons-material/Mood';
import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied';
import GroupIcon from '@mui/icons-material/Group';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import { SvgIconComponent } from '@mui/icons-material';
import { format } from 'date-fns';
import { DataTable } from '../components/DataTable';
import { FiltersBar, CheckboxListFilter, StatusIconConfig } from '../components/FiltersBar';
import { ProgressBar } from '../components/ProgressBar';
import { ActionsTableSkeleton } from './ActionsTableSkeleton';
import { SleepTimeEditor } from './SleepTimeEditor';
import { DownloadButton } from './DownloadButton';
import { GroupActionButtons } from './GroupActionButtons';
import { UserBox } from './UserBox';
import { colors } from '@src/styles';
import {
  ACTION_BUTTON_HEIGHT,
  dropdownPaperSX,
  filterFieldSX,
  monoFontFamily,
  monoLabelSX,
  skeletonSX,
  titleFontFamily,
} from '../scriptsStyles';
import { ResponseDetailsModal } from './ResponseDetailModal';
import {
  getActionsByScriptId,
  getTeamMembers,
  buildUserNamesFromTeamMembers,
  approveScript,
  runScript,
  cancelScript,
  uncancelScript,
  deleteScriptRequest,
  downloadScriptExport,
  updateExecutionMilliseconds,
  ActionFilters,
  getLayersByScriptId,
} from '../hooks/scripts';
import { ExportOptionsModal } from './ExportOptionsModal';
import { ReportsListModal } from './ReportsListModal';
import { getProgressItems } from '../utils/getProgressItems';
import { getRequestColumns } from '../utils/getRequestColumns';
import { useError } from '@src/contexts/ErrorContext';
import { ActionDTO } from '@src/services/tools/modules/v1/scripts/dto/tools-requests.dto';
import { useScriptsContext, ScriptRequestStatus } from '../context/ScriptsContext';

const MIN_MS = 1000;
const MAX_MS = 600000;

const formatEstimatedTime = (milliseconds: number): string => {
  const seconds = milliseconds / 1000;

  if (seconds < 60) {
    return `${seconds.toFixed(1)} s`;
  }

  const minutes = seconds / 60;
  if (minutes < 60) {
    return `${minutes.toFixed(1)} min`;
  }

  const hours = minutes / 60;
  if (hours < 24) {
    return `${hours.toFixed(1)} h`;
  }

  const days = hours / 24;
  return `${days.toFixed(1)} dias`;
};

const ACTION_STATUS_ICON_CONFIG: Record<string, StatusIconConfig> = {
  PENDING: { icon: HourglassEmptyIcon, color: colors.brand.textSecondary },
  WAITING: { icon: GroupIcon, color: colors.primary.yellow },
  RUNNING: { icon: SyncIcon, color: colors.status.running },
  CONCLUDED: { icon: MoodIcon, color: colors.status.concluded },
  FAILED: { icon: SentimentVeryDissatisfiedIcon, color: colors.status.rejected },
  SKIPPED: { icon: SkipNextIcon, color: '#F97316' },
};

const STATUS_CONFIG: Record<ScriptRequestStatus, { label: string; color: string; icon: SvgIconComponent }> = {
  PENDING: { label: 'Pendente', color: colors.brand.textSecondary, icon: HourglassEmptyIcon },
  RUNNING: { label: 'Executando', color: colors.status.pending, icon: SyncIcon },
  BLOCKED: { label: 'Bloqueado', color: colors.status.rejected, icon: BlockIcon },
  FINISHED: { label: 'Finalizado', color: colors.status.concluded, icon: CheckCircleIcon },
  FAILED: { label: 'Falhou', color: colors.primary.warning, icon: ErrorIcon },
};

const ScriptStatusBadge = ({
  status,
  isLoading,
  failureReason,
}: {
  status?: ScriptRequestStatus;
  isLoading: boolean;
  failureReason?: string | null;
}) => {
  if (isLoading) {
    return (
      <Skeleton variant="rounded" width={92} height={22} sx={{ ...skeletonSX, borderRadius: '999px', flexShrink: 0 }} />
    );
  }
  if (!status) return null;
  const { label, color, icon: Icon } = STATUS_CONFIG[status] ?? STATUS_CONFIG.PENDING;
  const tooltip = status === 'FAILED' && failureReason ? failureReason : '';
  return (
    <Tooltip title={tooltip} arrow placement="bottom">
      <Box
        component="span"
        sx={{
          flexShrink: 0,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.625,
          height: 22,
          px: 1,
          borderRadius: '999px',
          border: `1px solid ${color}55`,
          backgroundColor: `${color}14`,
          color,
          fontFamily: monoFontFamily,
          fontSize: '10px',
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          whiteSpace: 'nowrap',
          cursor: tooltip ? 'help' : 'default',
        }}
      >
        <Icon sx={{ color: `${color} !important`, fontSize: '12px' }} />
        {label}
      </Box>
    </Tooltip>
  );
};

const MetaDivider = () => (
  <Box aria-hidden sx={{ width: '1px', alignSelf: 'stretch', minHeight: 20, backgroundColor: colors.brand.line }} />
);

const sectionCardSX = {
  borderRadius: '14px',
  border: `1px solid ${colors.brand.line}`,
  backgroundColor: colors.brand.surface,
} as const;

const sortByOptionsMap: { [key: string]: ActionFilters['sortBy'] } = {
  startedAt: 'started_at',
  finishedAt: 'finished_at',
  status: 'status',
  url: 'url',
  method: 'method',
  layer: 'layer',
};

export const RequestsDetail = () => {
  const { setError, setSuccess } = useError();
  const {
    selectedScript: script,
    isLoadingScript,
    organizationNames,
    actionsSummary,
    scriptStatus,
    isLoadingSummary,
    isLoadingScriptStatus,
    refreshScriptData,
    handleBack: onBack,
  } = useScriptsContext();

  const [searchUrl, setSearchUrl] = useState('');
  const [searchBody, setSearchBody] = useState('');
  const [searchLayer, setSearchLayer] = useState<number | null>(null);
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [openDetailsModal, setOpenDetailsModal] = useState(false);
  const [selectedActionId, setSelectedActionId] = useState<string | null>(null);
  const [openExportModal, setOpenExportModal] = useState(false);
  const [openReportsModal, setOpenReportsModal] = useState(false);
  const [autoRefreshReports, setAutoRefreshReports] = useState(false);

  const [actions, setActions] = useState<ActionDTO[]>([]);
  const [userNames, setUserNames] = useState<Record<string, string>>({});
  const [layers, setLayers] = useState<{ value: string; label: string }[]>([]);
  const [isLoadingActions, setIsLoadingActions] = useState(true);
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [isLoadingLayers, setIsLoadingLayers] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(1);
  const [searchStatus, setSearchStatus] = useState<string[]>([]);
  const [isBlocking, setIsBlocking] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isRunningScript, setIsRunningScript] = useState(false);
  const [isDeletingScript, setIsDeletingScript] = useState(false);
  const filtersChangedRef = useRef<boolean>(false);

  useEffect(() => {
    filtersChangedRef.current = true;
    setCurrentPage(1);
  }, [searchUrl, searchBody, searchStatus, searchLayer, sortBy, sortOrder]);

  useEffect(() => {
    const fetchActions = async () => {
      if (!script?.id) return;
      if (filtersChangedRef.current && currentPage !== 1) return;

      setIsLoadingActions(true);
      try {
        const filters: ActionFilters = {
          currentPage,
          limit: 10,
          sortBy: sortBy ? sortByOptionsMap[sortBy as keyof typeof sortByOptionsMap] : undefined,
          sortOrder: sortBy ? sortOrder : undefined,
        };
        if (searchUrl.trim()) filters.url = searchUrl.trim();
        if (searchBody.trim()) filters.body = searchBody.trim();
        if (searchStatus.length > 0) filters.status = searchStatus;
        if (searchLayer !== null) filters.layer = searchLayer;

        const response = await getActionsByScriptId(script!.id, filters);
        setActions(response.items);
        setTotal(response.total);
        setTotalPages(response.totalPages);
      } catch (error) {
        console.error('Error fetching actions:', error);
        setError({
          title: 'Erro ao carregar actions',
          message: error?.response?.data?.message || error?.message || 'Não foi possível carregar as actions do script',
          statusCode: error?.response?.status,
        });
        setActions([]);
      } finally {
        setIsLoadingActions(false);
        if (filtersChangedRef.current && currentPage === 1) {
          filtersChangedRef.current = false;
        }
      }
    };
    fetchActions();
  }, [script?.id, currentPage, searchUrl, searchBody, searchStatus, searchLayer, scriptStatus, sortBy, sortOrder]);
  useEffect(() => {
    const fetchUserNames = async () => {
      setIsLoadingUsers(true);
      try {
        const members = await getTeamMembers();
        setUserNames(buildUserNamesFromTeamMembers(members));
      } catch (error) {
        console.error('Error fetching team members:', error);
        setError({
          title: 'Erro ao carregar usuários',
          message:
            error?.response?.data?.message || error?.message || 'Não foi possível carregar os nomes dos usuários',
          statusCode: error?.response?.status,
        });
        setUserNames({});
      } finally {
        setIsLoadingUsers(false);
      }
    };
    fetchUserNames();
  }, []);

  useEffect(() => {
    const fetchLayers = async () => {
      if (!script?.id) return;
      setIsLoadingLayers(true);
      try {
        const response = await getLayersByScriptId(script.id);
        const layerOptions = response.layers.map((layer) => ({
          value: String(layer.layer),
          label: `Layer ${layer.layer} (${layer.actionCount} actions)`,
        }));
        setLayers(layerOptions);
      } catch (error) {
        console.error('Error fetching layers:', error);
        setError({
          title: 'Erro ao carregar layers',
          message: error?.response?.data?.message || error?.message || 'Não foi possível carregar as layers',
          statusCode: error?.response?.status,
        });
        setLayers([]);
      } finally {
        setIsLoadingLayers(false);
      }
    };
    fetchLayers();
  }, [script?.id]);

  if (!script && !isLoadingScript) return null;

  const requestActions = (actionId: string) => {
    setSelectedActionId(actionId);
    setOpenDetailsModal(true);
  };

  const progressItems = actionsSummary ? getProgressItems(actionsSummary) : [];
  const requestColumns = getRequestColumns(requestActions);

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const handleDownload = async (format: 'csv' | 'xlsx', resolveResponse: boolean) => {
    try {
      const filename = `${script.name}-export${resolveResponse ? '-with-responses' : ''}.${format}`;
      await downloadScriptExport(script.id, format, resolveResponse, filename);
      setSuccess({ title: 'Download concluído', message: `Arquivo ${filename} gerado com sucesso` });
    } catch (error) {
      setError({
        title: 'Erro no download',
        message: error?.response?.data?.message || error?.message || 'Não foi possível fazer o download do arquivo',
        statusCode: error?.response?.status,
      });
    }
  };

  const handleBlock = async () => {
    if (
      !window.confirm(
        `Tem certeza que deseja bloquear o script "${script.name}"?\n\nEsta ação impedirá a execução do script.`,
      )
    )
      return;
    setIsBlocking(true);
    try {
      await cancelScript(script.id);
      await refreshScriptData();
      setSuccess({ title: 'Script bloqueado', message: `O script "${script.name}" foi bloqueado com sucesso` });
    } catch (error) {
      setError({
        title: 'Erro ao bloquear script',
        message: error?.response?.data?.message || error?.message || 'Não foi possível bloquear o script',
        statusCode: error?.response?.status,
      });
    } finally {
      setIsBlocking(false);
    }
  };

  const handleApprove = async () => {
    if (
      !window.confirm(
        `Tem certeza que deseja aprovar o script "${script.name}"?\n\nApós a aprovação, o script poderá ser executado.`,
      )
    )
      return;
    setIsApproving(true);
    try {
      await approveScript(script.id);
      await refreshScriptData();
      setSuccess({ title: 'Script aprovado', message: `O script "${script.name}" foi aprovado com sucesso` });
    } catch (err) {
      setError({
        title: 'Erro ao aprovar script',
        message: err?.response?.data?.message || err?.message || 'Não foi possível aprovar o script',
        statusCode: err?.response?.status,
      });
    } finally {
      setIsApproving(false);
    }
  };

  const handleRun = async () => {
    const isBlocked = scriptStatus === 'BLOCKED';
    const confirmMessage = isBlocked
      ? `Tem certeza que deseja desbloquear o script "${script.name}"?\n\nO script poderá ser executado após o desbloqueio.`
      : `Tem certeza que deseja executar o script "${script.name}"?\n\nO script será executado imediatamente.`;

    if (!window.confirm(confirmMessage)) return;

    setIsRunningScript(true);
    try {
      if (isBlocked) {
        await uncancelScript(script.id);
        await refreshScriptData();
        setSuccess({ title: 'Script desbloqueado', message: `O script "${script.name}" foi desbloqueado com sucesso` });
      } else {
        await runScript(script.id);
        await refreshScriptData();
        setSuccess({ title: 'Script em execução', message: `O script "${script.name}" foi iniciado` });
      }
    } catch (error) {
      setError({
        title: isBlocked ? 'Erro ao desbloquear script' : 'Erro ao executar script',
        message:
          error?.response?.data?.message ||
          error?.message ||
          `Não foi possível ${isBlocked ? 'desbloquear' : 'executar'} o script`,
        statusCode: error?.response?.status,
      });
    } finally {
      setIsRunningScript(false);
    }
  };

  const handleDelete = async () => {
    if (
      !window.confirm(`Tem certeza que deseja excluir o script "${script.name}"?\n\nEsta ação não pode ser desfeita.`)
    )
      return;
    setIsDeletingScript(true);
    try {
      await deleteScriptRequest(script.id);
      setSuccess({ title: 'Script excluído', message: `O script "${script.name}" foi excluído com sucesso` });
      onBack?.();
    } catch (error) {
      setError({
        title: 'Erro ao excluir script',
        message: error?.response?.data?.message || error?.message || 'Não foi possível excluir o script',
        statusCode: error?.response?.status,
      });
    } finally {
      setIsDeletingScript(false);
    }
  };

  const handleUpdateMilliseconds = async (milliseconds: number) => {
    try {
      await updateExecutionMilliseconds(script.id, milliseconds);
      setSuccess({ title: 'Intervalo atualizado', message: `Tempo de espera ajustado para ${milliseconds} ms` });
    } catch (error) {
      setError({
        title: 'Erro ao atualizar milissegundos',
        message: error?.response?.data?.message || error?.message || 'Não foi possível atualizar os milissegundos',
        statusCode: error?.response?.status,
      });
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        height: { md: 'calc(100vh - 32px)' },
        minHeight: 0,
        color: colors.primary.white,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', flexShrink: 0 }}>
        {onBack && (
          <Tooltip title="Voltar para a lista" arrow>
            <IconButton
              onClick={onBack}
              aria-label="Voltar para a lista de scripts"
              sx={{
                width: 34,
                height: 34,
                flexShrink: 0,
                borderRadius: '9px',
                border: `1px solid ${colors.brand.line}`,
                color: colors.brand.textSecondary,
                '& svg': { color: 'inherit', fontSize: 19 },
                '&:hover': {
                  borderColor: colors.primary.yellow,
                  color: colors.primary.yellow,
                  bgcolor: 'rgba(255, 160, 21, 0.08)',
                },
              }}
            >
              <ArrowBackRoundedIcon />
            </IconButton>
          </Tooltip>
        )}

        <Box sx={{ flex: '1 1 320px', minWidth: 0 }}>
          {isLoadingScript ? (
            <>
              <Skeleton variant="text" width="40%" height={28} sx={skeletonSX} />
              <Skeleton variant="text" width="55%" height={16} sx={skeletonSX} />
            </>
          ) : (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                <Typography
                  component="h1"
                  title={script!.name}
                  sx={{
                    fontFamily: titleFontFamily,
                    fontSize: '19px',
                    fontWeight: 600,
                    letterSpacing: '-0.02em',
                    lineHeight: 1.3,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {script!.name}
                </Typography>
                {script?.setup?.templateKey && (
                  <Box
                    component="span"
                    title="Template canônico"
                    sx={{
                      flexShrink: 0,
                      px: 0.875,
                      height: 20,
                      display: 'inline-flex',
                      alignItems: 'center',
                      borderRadius: '6px',
                      border: `1px solid ${colors.brand.line}`,
                      fontFamily: monoFontFamily,
                      fontSize: '11px',
                      color: colors.brand.textSecondary,
                    }}
                  >
                    {script.setup.templateKey}
                  </Box>
                )}
                <ScriptStatusBadge
                  status={scriptStatus}
                  isLoading={isLoadingScriptStatus}
                  failureReason={script?.execution?.failureReason}
                />
              </Box>
              <Typography
                title={script!.description || undefined}
                sx={{
                  mt: 0.25,
                  fontSize: '12.5px',
                  color: colors.brand.textSecondary,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {script!.execution?.organizationId && (
                  <Box component="span" sx={{ color: colors.brand.textStrong }}>
                    {organizationNames[script!.execution.organizationId] ?? script!.execution.organizationId}
                  </Box>
                )}
                {script!.execution?.organizationId && script!.description && ' · '}
                {script!.description}
              </Typography>
            </>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          {isLoadingScript ? (
            <>
              {[96, 96, 92].map((width, i) => (
                <Skeleton
                  key={i}
                  variant="rounded"
                  width={width}
                  height={ACTION_BUTTON_HEIGHT}
                  sx={{ ...skeletonSX, borderRadius: '8px' }}
                />
              ))}
            </>
          ) : (
            <>
              <GroupActionButtons
                onBlock={handleBlock}
                onApprove={handleApprove}
                onRun={handleRun}
                isBlocking={isBlocking}
                isApproving={isApproving}
                isRunningScript={isRunningScript}
              />
              {actionsSummary && actionsSummary.total === actionsSummary.pending && (
                <Tooltip title="Excluir script" arrow placement="bottom">
                  <IconButton
                    onClick={handleDelete}
                    disabled={isDeletingScript}
                    aria-label="Excluir script"
                    sx={{
                      width: ACTION_BUTTON_HEIGHT,
                      height: ACTION_BUTTON_HEIGHT,
                      flexShrink: 0,
                      borderRadius: '8px',
                      border: `1px solid ${colors.brand.line}`,
                      color: colors.status.rejected,
                      '& svg': { color: 'inherit', fontSize: 17 },
                      '&:hover': { borderColor: colors.status.rejected, bgcolor: `${colors.status.rejected}14` },
                    }}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </Tooltip>
              )}
              <DownloadButton onClick={() => setOpenExportModal(true)} />
            </>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          ...sectionCardSX,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '8px 16px',
          px: 2,
          py: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          {isLoadingScript ? (
            <>
              <Skeleton variant="text" width={110} height={18} sx={skeletonSX} />
              <Skeleton
                variant="rounded"
                width={150}
                height={ACTION_BUTTON_HEIGHT}
                sx={{ ...skeletonSX, borderRadius: '8px' }}
              />
            </>
          ) : (
            <>
              <Typography
                sx={{
                  fontFamily: monoFontFamily,
                  fontSize: '11.5px',
                  color: colors.brand.textSecondary,
                  whiteSpace: 'nowrap',
                }}
              >
                {format(new Date(script!.createdAt), 'dd/MM/yyyy HH:mm')}
              </Typography>
              <MetaDivider />
              <Typography sx={monoLabelSX}>Intervalo</Typography>
              <SleepTimeEditor
                value={script!.execution?.sleepTimeMs || MIN_MS}
                min={MIN_MS}
                max={MAX_MS}
                step={100}
                onSave={handleUpdateMilliseconds}
              />
              <Tooltip
                title="Tempo estimado para executar as actions filtradas (intervalo + 500 ms de margem por action)"
                arrow
              >
                <Typography sx={{ fontSize: '12px', color: colors.brand.textSecondary, whiteSpace: 'nowrap' }}>
                  Estimativa:{' '}
                  <Box component="span" sx={{ fontFamily: monoFontFamily, color: colors.brand.textStrong }}>
                    {formatEstimatedTime((script!.execution?.sleepTimeMs + 500) * total)}
                  </Box>
                  {/* O adicional de 500ms por request é um fator de segurança para variações no tempo de execução das requisições */}
                </Typography>
              </Tooltip>
            </>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <UserBox
            label="Criado por"
            userId={script?.createdByUserId ?? null}
            userNames={userNames}
            isLoadingUsers={isLoadingUsers || isLoadingScript}
          />
          {!script?.setup?.templateKey && (
            <>
              <MetaDivider />
              <UserBox
                label="Aprovado por"
                userId={script?.approvedByUserId ?? null}
                userNames={userNames}
                isLoadingUsers={isLoadingUsers || isLoadingScript}
              />
            </>
          )}
          <MetaDivider />
          <UserBox
            label="Bloqueado por"
            userId={script?.blockedByUserId ?? null}
            userNames={userNames}
            isLoadingUsers={isLoadingUsers || isLoadingScript}
          />
        </Box>
      </Box>

      {isLoadingSummary ? (
        <Box sx={{ ...sectionCardSX, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 1, px: 2, py: 1.5 }}>
          <Skeleton variant="text" width={130} height={14} sx={skeletonSX} />
          <Skeleton variant="rounded" height={12} sx={{ ...skeletonSX, width: '100%', borderRadius: 999 }} />
          <Box sx={{ display: 'flex', gap: 2 }}>
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} variant="text" width={96} height={14} sx={skeletonSX} />
            ))}
          </Box>
        </Box>
      ) : progressItems.length > 0 ? (
        <Box sx={{ ...sectionCardSX, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 1, px: 2, py: 1.5 }}>
          <Typography sx={{ ...monoLabelSX, whiteSpace: 'nowrap' }}>Status das actions</Typography>
          <ProgressBar items={progressItems} />
        </Box>
      ) : null}

      <Box
        sx={{
          ...sectionCardSX,
          flex: { md: '0 1 auto' },
          minHeight: { xs: 420, md: 0 },
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            px: 2,
            py: 1.25,
            flexWrap: 'wrap',
            borderBottom: `1px solid ${colors.brand.line}`,
          }}
        >
          <Typography sx={{ ...monoLabelSX, flexShrink: 0, mr: 0.5 }}>Actions</Typography>
          <FiltersBar searchValue={searchUrl} onSearchChange={setSearchUrl} searchPlaceholder="Buscar por URL…" />
          <FiltersBar searchValue={searchBody} onSearchChange={setSearchBody} searchPlaceholder="Buscar pelo body…" />
          <CheckboxListFilter
            label="Status"
            value={searchStatus}
            options={[
              { value: 'PENDING', label: 'Pendente' },
              { value: 'WAITING', label: 'Aguardando' },
              { value: 'RUNNING', label: 'Processando' },
              { value: 'CONCLUDED', label: 'Concluído' },
              { value: 'FAILED', label: 'Falho' },
              { value: 'SKIPPED', label: 'Ignorado' },
            ]}
            iconConfig={ACTION_STATUS_ICON_CONFIG}
            onChange={setSearchStatus}
            minWidth={130}
          />
          {isLoadingLayers ? (
            <Skeleton variant="rounded" width={130} height={36} sx={{ ...skeletonSX, borderRadius: '8px' }} />
          ) : (
            layers.length > 0 && (
              <FormControl size="small" sx={{ minWidth: 130, ...filterFieldSX }}>
                <Select
                  displayEmpty
                  value={searchLayer ?? ''}
                  onChange={(e) => setSearchLayer(e.target.value === '' ? null : Number(e.target.value))}
                  inputProps={{ 'aria-label': 'Layer' }}
                  renderValue={(selected) =>
                    String(selected) === '' ? (
                      <Box component="span" sx={{ color: colors.brand.textMuted }}>
                        Layer
                      </Box>
                    ) : (
                      <Box component="span" sx={{ fontFamily: monoFontFamily, fontSize: '12.5px' }}>
                        Layer {String(selected)}
                      </Box>
                    )
                  }
                  sx={{ '& .MuiSelect-select': { display: 'flex', alignItems: 'center', fontSize: '13px' } }}
                  MenuProps={{ PaperProps: { sx: dropdownPaperSX } }}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {layers.map((layer) => (
                    <MenuItem key={layer.value} value={layer.value}>
                      {layer.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )
          )}
        </Box>

        {isLoadingActions ? (
          <ActionsTableSkeleton rowCount={10} />
        ) : (
          <DataTable
            fillContainer
            data={actions}
            columns={requestColumns}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
            onRowClick={(row) => requestActions(row.id)}
          />
        )}
      </Box>

      <ResponseDetailsModal
        open={openDetailsModal}
        onClose={() => {
          setOpenDetailsModal(false);
          setSelectedActionId(null);
        }}
        scriptId={script?.id ?? ''}
        executionId={script?.id ?? ''}
        actionId={selectedActionId}
      />

      <ExportOptionsModal
        open={openExportModal}
        onClose={() => setOpenExportModal(false)}
        script={script!}
        onDownload={handleDownload}
        onViewReports={() => {
          setAutoRefreshReports(false);
          setOpenReportsModal(true);
        }}
        onExtractRequested={() => {
          setAutoRefreshReports(true);
        }}
      />

      <ReportsListModal
        open={openReportsModal}
        onClose={() => {
          setOpenReportsModal(false);
          setAutoRefreshReports(false);
        }}
        scriptId={script?.id ?? ''}
        autoRefresh={autoRefreshReports}
      />
    </Box>
  );
};
